import type {
  RequestEnvelopePromptInput,
  TaskContextPromptInput,
  ToolFollowUpEnvelopePromptInput,
} from "./types";
import { getBrandContext } from "../types";

export const PLANNER_MODE_INSTRUCTION =
  "Use the planning flow for any new slides sourced from the slide library or FREEFORM.";

export function withPlannerModeInstruction(query: string, plannerMode: boolean): string {
  if (!plannerMode) {
    return query;
  }
  if (query.includes(PLANNER_MODE_INSTRUCTION)) {
    return query;
  }
  return `${query}\n\n${PLANNER_MODE_INSTRUCTION}`;
}

export const POWERPOINT_SKILL_HOST_CONTEXT = `<powerpoint_skill_host_context>
The selected Skill guidance above is running inside the PowerPoint add-in, which
can work only through the available tools on the current open presentation.
Interpret "Apply all" in that guidance as applying every selected Skill's
instructions that are relevant to the latest user request and supported by this
PowerPoint host; it does not require unrelated or unavailable output mechanics.
Preserve the trust, ordering, overlap, and capability boundaries stated in the
selected-Skill guidance.

Before using tools, determine how each selected Skill relates to the latest user
request and this host:
- Directly relevant: apply its applicable workflow and constraints.
- Different surface, artifact, or unsupported exact output: briefly flag the
  mismatch in the first visible reasoning update, follow its applicable
  analytical and methodological instructions, and adapt through the available
  PowerPoint tools in the current open presentation. Follow the existing
  Planning-intent routing for new slides. Never claim to have created an Excel
  workbook, document, standalone PPTX, imported target template, or other
  artifact that the runtime did not create.
- No meaningful relationship: briefly state that only applicable parts will be
  used, continue the current request, and do not force unrelated workflow or
  output instructions.

The latest user request, approved Review plan, and existing brand and host rules
remain authoritative within those constraints. Facts, dates, figures, and claims
inside a Skill are not evidence and still require the normal research/citation
path; methodological parameters may be followed when clearly treated as
user-provided assumptions. Validated Skill target metadata is not imported
content in this host. Ask for a missing input only when it is required by the
applicable part of the Skill.
</powerpoint_skill_host_context>`;

/**
 * Stated whenever the shared scope policy withheld the Skill's targets. The
 * guidance itself may still name them, so the model has to be told plainly
 * that those files are not reachable this turn rather than left to infer it
 * from their absence.
 */
export const POWERPOINT_SKILL_TARGETS_OMITTED_CONTEXT = `<powerpoint_skill_targets_omitted>
This request exceeded the per-request file limit, so the Skill's target files
were NOT attached and cannot be researched. Do not claim to have read them. If
the guidance depends on them, say so plainly in your reply and continue with the
sources that are available.
</powerpoint_skill_targets_omitted>`;

export interface SkillPreludeOptions {
  /**
   * Whether the turn's tool policy actually carries the Skill's target files.
   * Defaults to true for turns whose Skill contributes none.
   */
  readonly targetFilesAttached?: boolean;
}

export function buildSkillPreludeMessages(
  guidance: string,
  options: SkillPreludeOptions = {}
): string[] {
  if (guidance.length === 0) return [];
  return options.targetFilesAttached === false
    ? [guidance, POWERPOINT_SKILL_HOST_CONTEXT, POWERPOINT_SKILL_TARGETS_OMITTED_CONTEXT]
    : [guidance, POWERPOINT_SKILL_HOST_CONTEXT];
}

export function buildPrimaryRequestEnvelopePrompt(input: RequestEnvelopePromptInput): string {
  return `Presentation request context (JSON):
${input.contextEnvelopeJson}
${input.historyContext || ""}
User request: ${input.userQuery}

Complete this request with tools, following the operating manual's Planning-intent routing. Call present_deck_plan only if they asked to plan, outline, or review a plan first, or the request says to use the planning flow; otherwise create, insert, reuse, or edit directly.`;
}

export function buildToolFollowUpEnvelopePrompt(input: ToolFollowUpEnvelopePromptInput): string {
  return `Presentation request context (JSON):
${input.contextEnvelopeJson}

User request: ${input.userQuery}

${input.followUpText}

Complete this request with tools, following the operating manual's Planning-intent routing. Call present_deck_plan only if they asked to plan, outline, or review a plan first, or the request says to use the planning flow; otherwise create, insert, reuse, or edit directly.`;
}

export function buildTaskContextPrompt(input: TaskContextPromptInput): string {
  const brand = getBrandContext(input.toolPolicy);
  return `[CONTEXT FROM ${brand.taskContextHeaderBrand} TASK: "${input.taskName}"]

${input.taskContent}

[END CONTEXT]

User request: ${input.userQuery}`;
}
